import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../stores/authStore";
import { useNotificationStore } from "../../stores/notificationStore";
import api from "../../api/client";
import {
  LayoutDashboard,
  LineChart,
  Settings,
  LogOut,
  Activity,
  Users,
  Sliders,
  FileText,
  Bell,
  KeyRound,
  History,
  Menu,
  X,
  Shield,
  Copy,
  BookOpen,
  Grid3X3,
  Key,
  Webhook,
  Store,
  Brain,
  Rocket,
  Wallet,
} from "lucide-react";
import { useWebSocket } from "../../hooks/useWebSocket";
import clsx from "clsx";

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/portfolio", icon: Wallet, label: "Portfolio" },
  { to: "/trades", icon: History, label: "Trades" },
  { to: "/risk", icon: Shield, label: "Risk" },
  { to: "/copy-trading", icon: Copy, label: "Copy Trade" },
  { to: "/journal", icon: BookOpen, label: "Journal" },
  { to: "/ai-insights", icon: Brain, label: "AI Insights" },
  { to: "/moonshot", icon: Rocket, label: "Moonshot" },
  { to: "/grid-trading", icon: Grid3X3, label: "Grid" },
  { to: "/backtest", icon: LineChart, label: "Backtest" },
  { to: "/settings", icon: Settings, label: "Settings" },
  { to: "/exchange", icon: KeyRound, label: "Exchange" },
  { to: "/api-keys", icon: Key, label: "API Keys" },
  { to: "/webhooks", icon: Webhook, label: "Webhooks" },
  { to: "/marketplace", icon: Store, label: "Marketplace" },
  { to: "/notifications", icon: Bell, label: "Alerts" },
];

const adminItems = [
  { to: "/admin", icon: Activity, label: "Overview", end: true },
  { to: "/admin/users", icon: Users, label: "Users" },
  { to: "/admin/config", icon: Sliders, label: "Config" },
  { to: "/admin/audit", icon: FileText, label: "Audit Log" },
];

function SidebarContent({ user, connected, onLogout, onNavClick, totalPnl, unreadCount }) {
  return (
    <>
      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        {navItems.map(({ to, icon: Icon, label, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onNavClick}
            className={({ isActive }) =>
              clsx(
                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
                isActive
                  ? "bg-dark-700 text-white"
                  : "text-text-muted hover:bg-dark-700/50 hover:text-text-primary",
              )
            }
          >
            <Icon className="h-4 w-4 shrink-0" />
            {label}
            {/* Bell badge */}
            {label === "Alerts" && unreadCount > 0 && (
              <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-red px-1.5 text-[10px] font-bold text-white">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </NavLink>
        ))}

        {user?.role === "admin" && (
          <>
            <div className="my-3 border-t border-dark-600" />
            <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
              Admin
            </p>
            {adminItems.map(({ to, icon: Icon, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={onNavClick}
                className={({ isActive }) =>
                  clsx(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
                    isActive
                      ? "bg-dark-700 text-white"
                      : "text-text-muted hover:bg-dark-700/50 hover:text-text-primary",
                  )
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                {label}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      <div className="border-t border-dark-600 p-3">
        {/* PnL Ticker */}
        {totalPnl !== null && totalPnl !== undefined && (
          <div className="mb-2 px-3">
            <p className="text-[10px] text-text-muted">Portfolio PnL</p>
            <p
              className={clsx(
                "font-mono text-sm font-bold",
                totalPnl >= 0 ? "text-accent-green" : "text-accent-red",
              )}
            >
              {totalPnl >= 0 ? "+" : ""}
              {totalPnl.toFixed(2)}%
            </p>
          </div>
        )}

        <div className="mb-2 flex items-center gap-2 px-3">
          <div
            className={clsx(
              "h-2 w-2 rounded-full",
              connected ? "bg-accent-green" : "bg-accent-red",
            )}
          />
          <span className="text-xs text-text-muted">
            {connected ? "Live" : "Disconnected"}
          </span>
        </div>
        {user && (
          <p className="mb-2 truncate px-3 text-xs text-text-muted">
            {user.email}
          </p>
        )}
        <button
          onClick={onLogout}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-text-muted transition-colors hover:bg-dark-700/50 hover:text-accent-red"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          Logout
        </button>
      </div>
    </>
  );
}

export default function DashboardLayout() {
  const logout = useAuthStore((s) => s.logout);
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const { connected, lastMessage } = useWebSocket();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [totalPnl, setTotalPnl] = useState(null);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const addNotification = useNotificationStore((s) => s.addNotification);

  // Load initial PnL from API
  useEffect(() => {
    api
      .getPortfolioStats()
      .then((stats) => {
        if (stats.total_pnl_pct !== undefined) {
          setTotalPnl(stats.total_pnl_pct);
        }
      })
      .catch(() => {});
  }, []);

  // Handle WS events for notifications + PnL updates
  useEffect(() => {
    if (!lastMessage) return;

    if (lastMessage.type === "signal") {
      const d = lastMessage.data;
      addNotification({
        type: "signal",
        title: `${d.direction || "LONG"} ${d.symbol || ""}`,
        message: d.score ? `Score: ${d.score}` : undefined,
      });
    } else if (lastMessage.type === "trade_update") {
      const d = lastMessage.data;
      const pnl = d.pnl_pct;
      addNotification({
        type: "trade",
        title: `${d.symbol || ""} ${d.exit_reason || "closed"}`,
        message:
          pnl !== undefined && pnl !== null
            ? `PnL: ${pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}%`
            : undefined,
      });
    } else if (lastMessage.type === "portfolio") {
      const d = lastMessage.data;
      if (d.total_pnl_pct !== undefined) {
        setTotalPnl(d.total_pnl_pct);
      }
    }
  }, [lastMessage, addNotification]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="flex h-dvh overflow-hidden">
      {/* Desktop Sidebar (md+) */}
      <aside className="hidden md:flex w-56 shrink-0 flex-col border-r border-dark-600 bg-dark-800">
        <div className="flex h-14 items-center gap-2 border-b border-dark-600 px-4">
          <Activity className="h-6 w-6 text-accent-green" />
          <span className="text-lg font-bold text-white">Temto</span>
          {/* Desktop PnL badge */}
          {totalPnl !== null && totalPnl !== undefined && (
            <span
              className={clsx(
                "ml-auto font-mono text-xs font-bold",
                totalPnl >= 0 ? "text-accent-green" : "text-accent-red",
              )}
            >
              {totalPnl >= 0 ? "+" : ""}
              {totalPnl.toFixed(2)}%
            </span>
          )}
        </div>
        <SidebarContent
          user={user}
          connected={connected}
          onLogout={handleLogout}
          totalPnl={totalPnl}
          unreadCount={unreadCount}
        />
      </aside>

      {/* Mobile Overlay (< md) */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={closeMobile}
          onKeyDown={(e) => e.key === "Escape" && closeMobile()}
          role="button"
          tabIndex={-1}
          aria-label="Close menu"
        />
      )}
      <aside
        className={clsx(
          "fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-dark-800 shadow-2xl transition-transform duration-200 md:hidden",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex h-14 items-center justify-between border-b border-dark-600 px-4">
          <div className="flex items-center gap-2">
            <Activity className="h-6 w-6 text-accent-green" />
            <span className="text-lg font-bold text-white">Temto</span>
          </div>
          <button
            onClick={closeMobile}
            className="rounded-lg p-1.5 text-text-muted hover:bg-dark-700 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <SidebarContent
          user={user}
          connected={connected}
          onLogout={handleLogout}
          onNavClick={closeMobile}
          totalPnl={totalPnl}
          unreadCount={unreadCount}
        />
      </aside>

      {/* Main Content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile top bar */}
        <header className="flex h-14 items-center gap-3 border-b border-dark-600 bg-dark-800 px-4 md:hidden">
          <button
            onClick={() => setMobileOpen(true)}
            className="rounded-lg p-1.5 text-text-muted hover:bg-dark-700 hover:text-white"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Activity className="h-5 w-5 text-accent-green" />
          <span className="font-bold text-white">Temto</span>

          {/* Mobile PnL ticker */}
          {totalPnl !== null && totalPnl !== undefined && (
            <span
              className={clsx(
                "font-mono text-xs font-bold",
                totalPnl >= 0 ? "text-accent-green" : "text-accent-red",
              )}
            >
              {totalPnl >= 0 ? "+" : ""}
              {totalPnl.toFixed(2)}%
            </span>
          )}

          <div className="ml-auto flex items-center gap-2">
            {/* Bell icon with badge */}
            <NavLink
              to="/notifications"
              className="relative rounded-lg p-1.5 text-text-muted hover:bg-dark-700 hover:text-white"
            >
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-red px-1 text-[9px] font-bold text-white">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </NavLink>
            <div
              className={clsx(
                "h-2 w-2 rounded-full",
                connected ? "bg-accent-green" : "bg-accent-red",
              )}
            />
            <span className="text-[10px] text-text-muted">
              {connected ? "Live" : "Off"}
            </span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
